import { opportunityDescription, opportunityFieldLabel, opportunityValueLabel } from "./opportunityPresentation";
/**
 * R5-B: read-only Opportunity Analyst inside the existing Control Center.
 *
 * A job can be `succeeded` while every opportunity record is still human-review
 * pending. Those are different statuses and the UI shows both. There is deliberately
 * no approve, promote, send, schedule or outreach control.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  api,
  ApiError,
  formatMicros,
  Job,
  JobDetail,
  OpportunityFieldCreateInput,
  OpportunityFieldSettings,
  OpportunityFieldUpdateInput,
  OpportunityFieldValueDocument,
  OpportunityFieldValuePage,
  OpportunityFieldValuePatchInput,
  OpportunityFieldsLoader,
  OpportunityGroup,
  OpportunityGroupCreateInput,
  OpportunityGroupPage,
  OpportunityGroupUpdateInput,
  OpportunityPersonalView,
  OpportunityPersonalViewEnvelope,
  OpportunityPersonalViewInput,
  OpportunityRecord,
  OpportunitySharedLayoutInput,
  OpportunityResult,
  OpportunityResultAvailable,
} from "../api/client";
import { OpportunityBrowser, groupErrorText } from "./OpportunityBrowser";
import { OpportunityPanelToggle } from "./OpportunityPanelToggle";
import { loadAllOpportunityGroups, opportunityGroupSourceLabel } from "./opportunityGroups";
import type { OpportunityGroupsLoader } from "./opportunityGroups";
import type { GroupAnalysisLoader } from "./groupAnalysis";
import {
  opportunityOutcomeLabel,
  opportunityReasons,
  opportunitySummaryMatches,
  opportunityTypeLabel,
  opportunityUnavailableReason,
  OpportunityStatus,
  parseOpportunityResult,
  safeOpportunityUrl,
} from "./opportunityResult";

export interface OpportunityJobPage {
  items: Job[];
  next_cursor?: string | null;
}

export interface OpportunityLoader {
  listJobs(
    projectId: string,
    options?: { cursor?: string | null },
  ): Promise<OpportunityJobPage>;
  getJob(projectId: string, jobId: string): Promise<JobDetail>;
  startOpportunityJob?(
    projectId: string,
    input: { idempotency_key: string },
  ): Promise<Job>;
  listOpportunityGroups?(
    projectId: string,
    options?: { cursor?: string | null; limit?: number },
  ): Promise<OpportunityGroupPage>;
  createOpportunityGroup?(
    projectId: string,
    input: OpportunityGroupCreateInput,
  ): Promise<OpportunityGroup>;
  updateOpportunityGroup?(
    projectId: string,
    groupId: string,
    input: OpportunityGroupUpdateInput,
  ): Promise<OpportunityGroup>;
  getGroupAnalysisPreflight?(projectId: string, groupId: string): Promise<unknown>;
  startGroupAnalysis?(
    projectId: string,
    groupId: string,
    input: { group_version: number; question: string; idempotency_key: string },
  ): Promise<unknown>;
  listGroupAnalysisRuns?(
    projectId: string,
    groupId: string,
    options?: { cursor?: string | null; limit?: number },
  ): Promise<unknown>;
  getOpportunityFieldSettings?(projectId: string): Promise<OpportunityFieldSettings>;
  createOpportunityField?(
    projectId: string,
    input: OpportunityFieldCreateInput,
  ): Promise<OpportunityFieldSettings>;
  updateOpportunityField?(
    projectId: string,
    fieldId: string,
    input: OpportunityFieldUpdateInput,
  ): Promise<OpportunityFieldSettings>;
  putOpportunitySharedLayout?(
    projectId: string,
    input: OpportunitySharedLayoutInput,
  ): Promise<OpportunityFieldSettings>;
  listOpportunityFieldValues?(
    projectId: string,
    options: { source_job_id: string; source_decisions_hash: string },
  ): Promise<OpportunityFieldValuePage>;
  patchOpportunityFieldValues?(
    projectId: string,
    organizationId: string,
    input: OpportunityFieldValuePatchInput,
  ): Promise<OpportunityFieldValueDocument>;
  getOpportunityPersonalView?(
    projectId: string,
  ): Promise<OpportunityPersonalViewEnvelope>;
  putOpportunityPersonalView?(
    projectId: string,
    input: OpportunityPersonalViewInput,
  ): Promise<OpportunityPersonalView>;
}

const JOB_STATUS_HR: Record<string, string> = {
  queued: "u redu",
  running: "izvršava se",
  awaiting_approval: "čeka odobrenje",
  awaiting_budget_approval: "čeka budžet",
  succeeded: "uspješno",
  failed: "neuspješno",
  cancelled: "otkazano",
  killed: "zaustavljeno",
  needs_review: "za pregled",
};

const TERMINAL = new Set(["succeeded", "failed", "cancelled", "killed", "needs_review"]);
const groupInputClass = "rounded border border-border bg-background px-3 py-2 text-sm";

function errorText(error: unknown): string {
  return error instanceof ApiError ? error.messageHr : String(error);
}

function isOpportunityJob(job: Job): boolean {
  return job.workflow === "megabooker.opportunity_analysis";
}

export function OpportunityPanel({
  projectId,
  canStart,
  currentUserId,
  canManageFields = false,
  loader = api,
}: {
  projectId: string;
  canStart: boolean;
  currentUserId?: string;
  canManageFields?: boolean;
  loader?: OpportunityLoader;
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [job, setJob] = useState<JobDetail | null>(null);
  const [empty, setEmpty] = useState(false);
  const [busy, setBusy] = useState(false);
  const generation = useRef(0);

  const loadLatest = useCallback(async () => {
    const current = ++generation.current;
    setLoading(true);
    setError(null);
    setEmpty(false);
    // Clear immediately on a project switch: even one frame of the previous
    // project's result would be a cross-project leak.
    setJob(null);
    try {
      const jobs: Job[] = [];
      const usedCursors = new Set<string>();
      let cursor: string | null = null;
      for (;;) {
        const listing = await loader.listJobs(projectId, { cursor });
        if (current !== generation.current) return;
        jobs.push(...listing.items.filter(isOpportunityJob));
        const next = listing.next_cursor ?? null;
        if (!next) break;
        if (usedCursors.has(next)) throw new Error("job_pagination_cursor_repeat");
        usedCursors.add(next);
        cursor = next;
      }
      jobs.sort((left, right) => {
        const byCreated = String(right.created_at).localeCompare(
          String(left.created_at),
        );
        if (byCreated !== 0) return byCreated;
        // The listing has no sub-second ordering. On a tie, do not let a succeeded
        // row outrank an in-flight/failed run and hide its current state.
        const leftSucceeded = left.status === "succeeded" ? 1 : 0;
        const rightSucceeded = right.status === "succeeded" ? 1 : 0;
        return leftSucceeded - rightSucceeded;
      });
      // Newest run wins regardless of status. Preferring an older succeeded job
      // would replace a newer running/failed run with stale success and stop the
      // poll that is following the active analysis.
      const candidate = jobs[0];
      if (!candidate) {
        if (current === generation.current) setEmpty(true);
        return;
      }
      const detail = await loader.getJob(projectId, candidate.id);
      if (current === generation.current) setJob(detail);
    } catch (err) {
      if (current === generation.current) setError(err);
    } finally {
      if (current === generation.current) setLoading(false);
    }
  }, [projectId, loader]);

  const groupsLoader: OpportunityGroupsLoader | undefined =
    loader.listOpportunityGroups &&
    loader.createOpportunityGroup &&
    loader.updateOpportunityGroup
      ? (loader as OpportunityGroupsLoader)
      : undefined;

  const groupAnalysisLoader: GroupAnalysisLoader | undefined =
    loader.getGroupAnalysisPreflight &&
    loader.startGroupAnalysis &&
    loader.listGroupAnalysisRuns
      ? (loader as unknown as GroupAnalysisLoader)
      : undefined;

  const fieldsLoader: OpportunityFieldsLoader | undefined =
    loader.getOpportunityFieldSettings &&
    loader.createOpportunityField &&
    loader.updateOpportunityField &&
    loader.putOpportunitySharedLayout &&
    loader.listOpportunityFieldValues &&
    loader.patchOpportunityFieldValues &&
    loader.getOpportunityPersonalView &&
    loader.putOpportunityPersonalView
      ? (loader as OpportunityFieldsLoader)
      : undefined;

  const openSourceJob = useCallback(
    async (sourceJobId: string) => {
      // Opening an older group's source analysis is an explicit navigation, not a
      // silent rebind. It uses the same generation guard as every other load so a
      // project switch invalidates the continuation.
      const current = ++generation.current;
      setLoading(true);
      setError(null);
      setEmpty(false);
      setBusy(false);
      try {
        const detail = await loader.getJob(projectId, sourceJobId);
        if (current !== generation.current) return;
        if (detail.project_id !== projectId) {
          throw new Error("project_mismatch");
        }
        setJob(detail);
      } catch (err) {
        if (current === generation.current) setError(err);
      } finally {
        if (current === generation.current) setLoading(false);
      }
    },
    [projectId, loader],
  );

  useEffect(() => {
    void loadLatest();
    return () => {
      // A project switch or unmount invalidates every in-flight request's result.
      generation.current += 1;
    };
  }, [loadLatest]);

  // A start request belongs to the project that issued it. A project switch must
  // clear the transient busy flag rather than leave the new project's controls
  // disabled by a continuation that is no longer allowed to touch this view.
  useEffect(() => {
    setBusy(false);
  }, [projectId]);

  // Poll only while this project's job is non-terminal. The generation guard and
  // the `project_id` check below make stale work unable to replace the current view.
  useEffect(() => {
    if (!job || job.project_id !== projectId || TERMINAL.has(job.status)) return;
    let active = true;
    const timer = window.setInterval(() => {
      void (async () => {
        try {
          const detail = await loader.getJob(projectId, job.id);
          if (active) setJob(detail);
        } catch (err) {
          if (active) setError(err);
        }
      })();
    }, 1500);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [job, projectId, loader]);

  const start = async () => {
    if (!loader.startOpportunityJob) return;
    // Capture the project generation before the POST. The whole continuation,
    // including refresh/error/busy updates, is invalid once the operator switches
    // project or unmounts this view.
    const current = ++generation.current;
    setBusy(true);
    setError(null);
    setEmpty(false);
    try {
      const created = await loader.startOpportunityJob(projectId, {
        idempotency_key: `r5b-${Date.now()}-${Math.floor(Math.random() * 10_000)}`,
      });
      if (current !== generation.current) return;
      // Bind the view to the job the POST actually created instead of listing and
      // falling back to an older succeeded run. The poll effect below follows this
      // exact id until it reaches a terminal state.
      setJob({ ...created, steps: [], result: null });
      setLoading(false);
      const detail = await loader.getJob(projectId, created.id);
      if (current !== generation.current) return;
      setJob(detail);
    } catch (err) {
      if (current === generation.current) {
        setError(err);
        setLoading(false);
      }
    } finally {
      if (current === generation.current) setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="opportunity-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Opportunity Analyst</h2>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
            Sintetička offline analiza 20 organizacija. Strojna klasifikacija nije
            ljudsko odobrenje; svaki zapis ostaje <strong>PENDING</strong> za ljudski
            pregled. Ovdje nema odobravanja, slanja ni outreach radnje.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void loadLatest()}
            disabled={loading || busy}
            className="rounded border border-border px-3 py-1.5 text-sm disabled:opacity-50"
            data-testid="opportunity-refresh"
          >
            Osvježi
          </button>
          <button
            type="button"
            onClick={() => void start()}
            disabled={!canStart || busy || !loader.startOpportunityJob}
            className="rounded bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50"
            data-testid="opportunity-start"
          >
            Pokreni analizu oportuniteta
          </button>
        </div>
      </div>

      {error != null && (
        <p className="rounded border border-destructive/40 bg-destructive/10 p-3 text-sm"
           role="alert" data-testid="opportunity-error">
          {errorText(error)}
        </p>
      )}
      {loading && (
        <p className="text-sm text-muted-foreground" data-testid="opportunity-loading">
          Učitavanje analize oportuniteta…
        </p>
      )}
      {!loading && empty && (
        <p className="rounded border border-border p-4 text-sm text-muted-foreground"
           data-testid="opportunity-empty">
          U ovom projektu nema R5 Opportunity Analyst posla. Pokrenite sintetičku analizu.
        </p>
      )}
      {!loading && job && (
        <OpportunityResultView
          key={job.id}
          result={job.result as OpportunityResult | null | undefined}
          jobStatus={job.status}
          jobId={job.id}
          projectId={projectId}
          jobProjectId={job.project_id}
          groupsLoader={groupsLoader}
          groupAnalysisLoader={groupAnalysisLoader}
          fieldsLoader={fieldsLoader}
          currentUserId={currentUserId}
          canManageFields={canManageFields}
          onOpenSourceJob={openSourceJob}
        />
      )}
    </div>
  );
}

export function OpportunityResultView({
  result,
  jobStatus,
  jobId,
  projectId,
  jobProjectId,
  groupsLoader,
  groupAnalysisLoader,
  fieldsLoader,
  currentUserId,
  canManageFields = false,
  onOpenSourceJob,
}: {
  result: OpportunityResult | null | undefined;
  jobStatus: string;
  jobId: string;
  projectId?: string;
  jobProjectId?: string;
  groupsLoader?: OpportunityGroupsLoader;
  groupAnalysisLoader?: GroupAnalysisLoader;
  fieldsLoader?: OpportunityFieldsLoader;
  currentUserId?: string;
  canManageFields?: boolean;
  onOpenSourceJob?: (jobId: string) => Promise<void> | void;
}) {
  const status = JOB_STATUS_HR[jobStatus] ?? jobStatus;
  const expectedProject = projectId ?? jobProjectId;
  // The outer job and the selected project must both agree before any nested
  // projection is considered; the projection itself is then bound to the same
  // project. A foreign nested record is unavailable, never rendered.
  if (
    projectId !== undefined &&
    jobProjectId !== undefined &&
    jobProjectId !== projectId
  ) {
    return (
      <UnavailableView
        jobStatus={status}
        jobId={jobId}
        reason="project_mismatch"
        projectId={projectId}
        groupsLoader={groupsLoader}
        onOpenSourceJob={onOpenSourceJob}
      />
    );
  }
  const parsed = parseOpportunityResult(result);
  if (parsed === null) {
    return (
      <UnavailableView
        jobStatus={status}
        jobId={jobId}
        reason={result == null ? undefined : "invalid_projection"}
        generic={result == null}
        projectId={projectId}
        groupsLoader={groupsLoader}
        onOpenSourceJob={onOpenSourceJob}
      />
    );
  }
  if (parsed.state === "unavailable") {
    return (
      <UnavailableView
        jobStatus={status}
        jobId={jobId}
        reason={parsed.reason}
        projectId={projectId}
        groupsLoader={groupsLoader}
        onOpenSourceJob={onOpenSourceJob}
      />
    );
  }
  if (expectedProject !== undefined && parsed.project_id !== expectedProject) {
    return (
      <UnavailableView
        jobStatus={status}
        jobId={jobId}
        reason="project_mismatch"
        projectId={projectId}
        groupsLoader={groupsLoader}
        onOpenSourceJob={onOpenSourceJob}
      />
    );
  }
  if (!opportunitySummaryMatches(parsed)) {
    return (
      <section className="rounded border border-destructive/40 p-4"
               data-testid="opportunity-arithmetic-error">
        <p className="text-sm" data-testid="opportunity-job-status">
          Status obrade posla: <strong>{status}</strong>{" "}
          <span className="font-mono text-xs text-muted-foreground">{jobId}</span>
        </p>
        <p className="mt-2 text-sm text-destructive">
          Sažetak se ne slaže sa zapisima. Prikaz je blokiran umjesto da se brojevi
          poprave u pregledniku.
        </p>
      </section>
    );
  }
  return (
    <OpportunityAvailableView
      result={parsed}
      jobStatus={status}
      jobId={jobId}
      projectId={expectedProject ?? parsed.project_id}
      groupsLoader={groupsLoader}
      groupAnalysisLoader={groupAnalysisLoader}
      fieldsLoader={fieldsLoader}
      currentUserId={currentUserId}
      canManageFields={canManageFields}
      onOpenSourceJob={onOpenSourceJob}
    />
  );
}

/**
 * Read-only directory of persisted project groups shown when the latest job's
 * projection is unavailable or failed. Team data belongs to the project, not to
 * the newest analysis, so a bad latest run must not hide it. Selecting a group
 * keeps the latest job's status on screen; opening its pinned source job is an
 * explicit navigation and never rebinds the group to a different analysis.
 */
function SharedProjectGroups({
  projectId,
  groupsLoader,
  onOpenSourceJob,
}: {
  projectId: string;
  groupsLoader: OpportunityGroupsLoader;
  onOpenSourceJob?: (jobId: string) => Promise<void> | void;
}) {
  const [groups, setGroups] = useState<OpportunityGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const generation = useRef(0);

  const load = useCallback(async () => {
    const current = ++generation.current;
    setLoading(true);
    setError("");
    setGroups([]);
    setSelectedId("");
    try {
      const loaded = await loadAllOpportunityGroups(
        groupsLoader,
        projectId,
        () => current === generation.current,
      );
      if (loaded === null || current !== generation.current) return;
      setGroups(loaded);
    } catch (err) {
      if (current !== generation.current) return;
      // A tampered/foreign/unbounded response never becomes a local group list.
      setGroups([]);
      setError(groupErrorText(err));
    } finally {
      if (current === generation.current) setLoading(false);
    }
  }, [groupsLoader, projectId]);

  useEffect(() => {
    void load();
    return () => {
      generation.current += 1;
    };
  }, [load]);

  const selected = groups.find((group) => group.id === selectedId);

  return (
    <section aria-label="Zajedničke grupe projekta"
             className="space-y-3 rounded border border-border bg-muted/10 p-4"
             data-testid="opportunity-groups-section">
      <h3 className="text-sm font-semibold">Zajedničke grupe projekta</h3>
      <p className="text-xs text-muted-foreground">
        Spremljene grupe pripadaju projektu i ostaju dostupne neovisno o statusu
        najnovije analize. Odabir grupe ne mijenja status najnovijeg posla.
      </p>
      {loading && (
        <p className="text-sm text-muted-foreground" role="status"
           data-testid="opportunity-groups-loading">
          Učitavanje grupa s projekta…
        </p>
      )}
      {error && (
        <div className="space-y-2" role="alert" data-testid="opportunity-groups-error">
          <p className="text-sm text-destructive">{error}</p>
          <button
            type="button"
            className="rounded border border-border px-3 py-2 text-sm"
            data-testid="opportunity-groups-refresh"
            onClick={() => void load()}
          >
            Osvježi popis grupa
          </button>
        </div>
      )}
      {!loading && !error && groups.length === 0 && (
        <p className="text-xs text-muted-foreground" data-testid="opportunity-groups-empty">
          Još nema spremljenih grupa na projektu.
        </p>
      )}
      {groups.length > 0 && (
        <label className="block text-sm">
          Prikaži grupu
          <select
            className={`${groupInputClass} ml-2`}
            data-testid="opportunity-group-select"
            value={selectedId}
            onChange={(event) => setSelectedId(event.target.value)}
          >
            <option value="">Odaberi spremljenu grupu</option>
            {groups.map((group) => (
              <option key={group.id} value={group.id}>
                {`${group.name} (${group.organization_ids.length}) · v${group.version}`}
              </option>
            ))}
          </select>
        </label>
      )}
      {selected && (
        <div className="rounded border border-border p-3 text-sm"
             data-testid="opportunity-group-card">
          <p className="font-medium">
            {selected.name} · {selected.organization_ids.length} zapisa · v
            {selected.version}
          </p>
          <p className="mt-1 text-xs text-muted-foreground"
             data-testid="opportunity-group-source">
            {opportunityGroupSourceLabel(selected)} · odluke{" "}
            <span className="break-all font-mono">
              {selected.source.decisions_hash}
            </span>
          </p>
          <p className="mt-1 whitespace-pre-wrap">{selected.note || "Nema bilješke."}</p>
          {onOpenSourceJob && (
            <button
              type="button"
              className="mt-2 rounded border border-border px-3 py-2"
              data-testid="opportunity-group-open-source"
              onClick={() => void onOpenSourceJob(selected.source.job_id)}
            >
              Otvori izvornu analizu ({selected.source.job_id})
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function UnavailableView({
  jobStatus,
  jobId,
  reason,
  generic = false,
  projectId,
  groupsLoader,
  onOpenSourceJob,
}: {
  jobStatus: string;
  jobId: string;
  reason?: string;
  generic?: boolean;
  projectId?: string;
  groupsLoader?: OpportunityGroupsLoader;
  onOpenSourceJob?: (jobId: string) => Promise<void> | void;
}) {
  return (
    <section className="rounded border border-border p-4"
             data-testid="opportunity-unavailable" data-reason={reason}>
      <p className="text-sm" data-testid="opportunity-job-status">
        Status obrade posla: <strong>{jobStatus}</strong>{" "}
        <span className="font-mono text-xs text-muted-foreground">{jobId}</span>
      </p>
      {generic || !reason ? (
        <p className="mt-2 text-sm text-muted-foreground">
          Ova projekcija rezultata nije dostupna. Status posla nije status zapisa.
        </p>
      ) : (
        <p className="mt-2 text-sm text-destructive"
           data-testid="opportunity-unavailable-reason">
          {opportunityUnavailableReason(reason)}
        </p>
      )}
      <p className="mt-1 text-xs text-muted-foreground">
        Ne prikazuju se djelomični zapisi, izvori ni trošak.
      </p>
      {projectId && groupsLoader && (
        <div className="mt-4">
          <SharedProjectGroups
            projectId={projectId}
            groupsLoader={groupsLoader}
            onOpenSourceJob={onOpenSourceJob}
          />
        </div>
      )}
    </section>
  );
}

function Metric({ label, value, testId }: { label: string; value: number; testId: string }) {
  return (
    <div className="rounded border border-border p-3" data-testid={testId}>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-2xl font-semibold">{value}</dd>
    </div>
  );
}

function OpportunityAvailableView({
  result,
  jobStatus,
  jobId,
  projectId,
  groupsLoader,
  groupAnalysisLoader,
  fieldsLoader,
  currentUserId,
  canManageFields = false,
  onOpenSourceJob,
}: {
  result: OpportunityResultAvailable;
  jobStatus: string;
  jobId: string;
  projectId: string;
  groupsLoader?: OpportunityGroupsLoader;
  groupAnalysisLoader?: GroupAnalysisLoader;
  fieldsLoader?: OpportunityFieldsLoader;
  currentUserId?: string;
  canManageFields?: boolean;
  onOpenSourceJob?: (jobId: string) => Promise<void> | void;
}) {
  const [status, setStatus] = useState<OpportunityStatus>("all");
  // Presentation only. The table stays the default focus; every detail below is
  // kept mounted (hidden) so no projection, filter or editing state is rebuilt.
  const [statusCollapsed, setStatusCollapsed] = useState(true);
  const [costCollapsed, setCostCollapsed] = useState(true);
  const measuredZeroCost =
    result.cost !== undefined &&
    result.cost.known &&
    result.cost.actual_micros === 0 &&
    result.cost.unavailable_count === 0;
  const filters: { id: OpportunityStatus; label: string; count: number }[] = [
    { id: "all", label: "Svi", count: result.summary.produced },
    { id: "draft_candidate", label: "Prijedlozi", count: result.summary.draft_candidate },
    { id: "needs_review", label: "Za pregled", count: result.summary.needs_review },
    { id: "duplicate", label: "Duplikati", count: result.summary.duplicate },
    { id: "rejected", label: "Odbijeni", count: result.summary.rejected },
  ];

  return (
    <section className="space-y-4" data-testid="opportunity-result">
      <section className="rounded border border-border p-4"
               data-testid="opportunity-status-panel">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <h3 className="text-sm font-semibold">Status obrade posla</h3>
          <OpportunityPanelToggle
            controls="opportunity-status-panel-body"
            expanded={!statusCollapsed}
            label="Status obrade posla"
            testId="opportunity-status-toggle"
            onToggle={() => setStatusCollapsed((value) => !value)}
          />
        </div>
        <div className="mt-2 space-y-1" data-testid="opportunity-status-summary">
          <p className="break-words text-sm" data-testid="opportunity-job-status-summary">
            Status: <strong>{jobStatus}</strong>{" "}
            <span className="break-all font-mono text-xs text-muted-foreground">{jobId}</span>
            {" · "}Neriješeni ljudski pregled:{" "}
            <strong>{result.summary.human_review_pending}/{result.summary.produced}</strong>{" "}
            (PENDING).
          </p>
          <p className="text-xs text-warn" data-testid="opportunity-status-warning">
            Posao može biti uspješan dok zapisi čekaju ljudski pregled. Strojna
            klasifikacija nije odobrenje.
          </p>
        </div>
        <div id="opportunity-status-panel-body" hidden={statusCollapsed}
             className="mt-3 space-y-3">
          <p className="text-sm" data-testid="opportunity-job-status">
            Status obrade posla: <strong>{jobStatus}</strong>{" "}
            <span className="font-mono text-xs text-muted-foreground">{jobId}</span>
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Posao može biti uspješan dok su svi zapisi još neriješeni. Strojna
            klasifikacija nije odobrenje i ovdje se ne može pretvoriti u odobrenje.
          </p>
          <p className="mt-2 rounded border border-warn/40 bg-warn/10 p-2 text-sm"
             data-testid="opportunity-human-pending">
            Ljudski pregled: <strong>PENDING</strong> ({result.summary.human_review_pending}/
            {result.summary.produced}). Zabilježene minute: nema (null).
          </p>
          <dl className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Metric label="Proizvedeno" value={result.summary.produced}
                    testId="opportunity-summary-produced" />
            <Metric label="Prijedlozi" value={result.summary.draft_candidate}
                    testId="opportunity-summary-draft" />
            <Metric label="Za pregled" value={result.summary.needs_review}
                    testId="opportunity-summary-needs-review" />
            <Metric label="Duplikati" value={result.summary.duplicate}
                    testId="opportunity-summary-duplicate" />
            <Metric label="Odbijeni" value={result.summary.rejected}
                    testId="opportunity-summary-rejected" />
            <Metric label="Ugovorno odbijeni" value={result.summary.contract_rejected}
                    testId="opportunity-summary-contract-rejected" />
          </dl>
          <p className="mt-3 text-xs text-muted-foreground">
            Fixture: {result.fixture.id} v{result.fixture.version} · referentni datum{" "}
            {result.evaluated_on} · snapshot{" "}
            <span className="break-all font-mono">{result.snapshot_hash}</span>
          </p>
        </div>
      </section>

      <section className="rounded border border-border p-4" data-testid="opportunity-cost">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <h3 className="text-sm font-semibold">Trošak ovog posla</h3>
          <OpportunityPanelToggle
            controls="opportunity-cost-panel-body"
            expanded={!costCollapsed}
            label="Trošak ovog posla"
            testId="opportunity-cost-toggle"
            onToggle={() => setCostCollapsed((value) => !value)}
          />
        </div>
        <div className="mt-1 break-words text-sm" data-testid="opportunity-cost-summary">
          {result.cost === undefined ? (
            <p className="text-muted-foreground">
              Nije dostupno bez ovlasti <code>budget.view</code>.
            </p>
          ) : !result.cost.known ? (
            <p className="text-warn" data-testid="opportunity-cost-summary-unknown">
              Trošak nije poznat. Nedostajući iznos nije nula.
            </p>
          ) : (
            <p className="flex flex-wrap gap-x-3 gap-y-1">
              <span>
                Stvarni: <strong data-testid="opportunity-cost-actual-summary">
                  {formatMicros(result.cost.actual_micros)}
                </strong>
              </span>
              <span>
                Procijenjeni: <strong data-testid="opportunity-cost-estimated-summary">
                  {formatMicros(result.cost.estimated_micros)}
                </strong>
              </span>
              <span>
                Nepoznatih stavki: <strong data-testid="opportunity-cost-unavailable-summary">
                  {result.cost.unavailable_count ?? 0}
                </strong>
              </span>
            </p>
          )}
        </div>
        <div id="opportunity-cost-panel-body" hidden={costCollapsed} className="mt-2">
          {result.cost === undefined ? (
            <p className="mt-1 text-sm text-muted-foreground">
              Trošak nije dostupan bez ovlasti <code>budget.view</code>.
            </p>
          ) : !result.cost.known ? (
            <p className="mt-1 text-sm text-warn" data-testid="opportunity-cost-unknown">
              Trošak nije poznat. Nedostajući iznos se ne prikazuje kao nula.
            </p>
          ) : (
            <dl className="mt-2 grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
              <div>
                <dt className="text-muted-foreground">Stvarni trošak</dt>
                <dd data-testid="opportunity-cost-actual">
                  {formatMicros(result.cost.actual_micros)}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Procijenjeni</dt>
                <dd>{formatMicros(result.cost.estimated_micros)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Nepoznatih stavki</dt>
                <dd>{result.cost.unavailable_count ?? 0}</dd>
              </div>
            </dl>
          )}
          {measuredZeroCost && (
            <p className="mt-2 text-xs text-muted-foreground"
               data-testid="opportunity-cost-measured-zero">
              Sintetički R5 put je stvarno izmjeren na nuli i ne stvara plaćene
              rezervacije ni vanjske zapise.
            </p>
          )}
        </div>
      </section>

      <div className="flex flex-wrap gap-2" data-testid="opportunity-filters">
        {filters.map((entry) => (
          <button
            key={entry.id}
            type="button"
            onClick={() => setStatus(entry.id)}
            aria-pressed={status === entry.id}
            className={`rounded px-3 py-1.5 text-sm ${
              status === entry.id
                ? "bg-primary text-primary-foreground"
                : "border border-border"
            }`}
            data-testid={`opportunity-filter-${entry.id}`}
          >
            {entry.label} ({entry.count})
          </button>
        ))}
      </div>

      <OpportunityBrowser
        key={`${result.project_id}:${jobId}:${result.review_output_hash}`}
        records={result.records}
        status={status}
        projectId={projectId}
        sourceJobId={jobId}
        sourceDecisionsHash={result.decisions_hash}
        groupsLoader={groupsLoader}
        groupAnalysisLoader={groupAnalysisLoader}
        fieldsLoader={fieldsLoader}
        currentUserId={currentUserId}
        canManageFields={canManageFields}
        onOpenSourceJob={onOpenSourceJob}
        onResetStatus={() => setStatus("all")}
        renderDetail={(record, index) => (
          <OpportunityDetail record={record} index={index} />
        )}
      />
    </section>
  );
}

function OpportunityDetail({
  record,
  index,
}: {
  record: OpportunityRecord;
  index: number;
}) {
  const name = record.organization_name ?? record.organization_id;
  return (
    <div data-testid={`opportunity-record-${index}`} data-outcome={record.outcome}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-sm font-semibold">{name}</p>
          <p className="font-mono text-xs text-muted-foreground">
            {record.organization_id}
          </p>
        </div>
        <span className="rounded border border-border px-2 py-1 text-xs">
          {opportunityOutcomeLabel(record.outcome)}
        </span>
      </div>

      <section className="mt-4 rounded border border-border bg-muted/20 p-3" aria-label="Kratki opis">
        <h4 className="text-sm font-semibold">Kratki opis</h4>
        <p className="mt-2 text-sm leading-relaxed">{opportunityDescription(record)}</p>
        <p className="mt-2 text-xs text-muted-foreground">Sažetak dostupnih izvora; nije dodatna analiza ni novi dokaz.</p>
      </section>
      <dl className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
        <div>
          <dt className="text-xs text-muted-foreground">Fit</dt>
          <dd data-testid={`opportunity-fit-${index}`}>
            {record.fit_score === null ? "—" : record.fit_score}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Pouzdanost</dt>
          <dd data-testid={`opportunity-confidence-${index}`}>
            {record.confidence === null ? "—" : record.confidence}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Tip organizacije</dt>
          <dd>{record.organization_type ? opportunityValueLabel(record.organization_type) : "—"}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Regija</dt>
          <dd>{record.region ? opportunityValueLabel(record.region) : "—"}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Tip prilike</dt>
          <dd data-testid={`opportunity-type-${index}`}>
            {opportunityTypeLabel(record.opportunity_type)}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Ljudski pregled</dt>
          <dd data-testid={`opportunity-human-${index}`}>Čeka ljudski pregled · vrijeme nije zabilježeno</dd>
        </div>
      </dl>

      <h4 className="mt-4 text-sm font-semibold">Zašto je zapis ovako procijenjen?</h4>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">{opportunityReasons(record).map((reason) => <li key={reason}>{reason}</li>)}</ul>
      {record.duplicate_of && (
        <p className="mt-1 text-xs text-muted-foreground">
          Duplikat od: <span className="font-mono">{record.duplicate_of}</span>
        </p>
      )}
      <p className="mt-1 text-xs text-muted-foreground">
        Nedostaje: {record.missing_fields.length > 0 ? record.missing_fields.map(opportunityFieldLabel).join(", ") : "ništa"}
      </p>
      {record.uncited_fields.length > 0 && (
        <p className="mt-1 text-xs text-muted-foreground">
          Necitirano: {record.uncited_fields.map(opportunityFieldLabel).join(", ")}
        </p>
      )}
      {record.contradictions.length > 0 && (
        <ul className="mt-1 space-y-1 text-xs text-muted-foreground">
          {record.contradictions.map((row) => (
            <li key={row.field}>
              Proturječje {opportunityFieldLabel(row.field)}: {row.values.map(opportunityValueLabel).join(" / ")}
            </li>
          ))}
        </ul>
      )}
      {record.suppression.suppressed && (
        <p className="mt-1 text-xs text-destructive">
          Zapis je isključen prema pravilima isključenja.
        </p>
      )}
      {record.contract_error_code && (
        <p className="mt-1 text-xs text-muted-foreground">
          Zapis nije prošao provjeru ispravnosti podataka.
        </p>
      )}

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            Verificirane činjenice
          </p>
          <ul className="mt-1 space-y-1 text-xs">
            {record.verified_facts.map((fact, factIndex) => (
              <li key={`${fact.field}-${factIndex}`}>
                {opportunityFieldLabel(fact.field)}: {opportunityValueLabel(fact.value)}
                <span className="ml-1 text-muted-foreground">
                  ({fact.source_ids.map((id) => `Izvor ${record.sources.findIndex((source) => source.source_id === id) + 1}`).join(", ")})
                </span>
              </li>
            ))}
            {record.verified_facts.length === 0 && (
              <li className="text-muted-foreground">Nema verificiranih činjenica.</li>
            )}
          </ul>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            Inferirano (nije dokaz)
          </p>
          <ul className="mt-1 space-y-1 text-xs">
            {record.inferred_facts.map((fact, factIndex) => (
              <li key={`${fact.field}-${factIndex}`}>
                {opportunityFieldLabel(fact.field)}: {opportunityValueLabel(fact.value)}
                <span className="ml-1 text-muted-foreground">(izvedeni zaključak; provjeriti prije upotrebe)</span>
              </li>
            ))}
            {record.inferred_facts.length === 0 && (
              <li className="text-muted-foreground">Nema inferiranih vrijednosti.</li>
            )}
          </ul>
        </div>
      </div>

      <div className="mt-3">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Izvori</p>
        <ul className="mt-1 space-y-2">
          {record.sources.map((source, sourceIndex) => {
            const href = safeOpportunityUrl(source.url);
            return (
              <li key={`${source.source_id}-${sourceIndex}`} className="text-xs">
                {href ? (
                  <a href={href} target="_blank" rel="noopener noreferrer"
                     className="break-all underline">
                    {source.url}
                  </a>
                ) : (
                  <span className="text-destructive">[izvor nije prikazan]</span>
                )}
                <span className="ml-2 text-muted-foreground">{source.observed_at}</span>
                <p className="mt-0.5 break-words">{source.claim}</p>
                <ul className="mt-0.5 text-muted-foreground">
                  {source.claims.map((claim, claimIndex) => (
                    <li key={`${claim.field}-${claimIndex}`}>
                      {opportunityFieldLabel(claim.field)}: {opportunityValueLabel(claim.value)}
                    </li>
                  ))}
                </ul>
              </li>
            );
          })}
          {record.sources.length === 0 && (
            <li className="text-xs text-muted-foreground">Nema izvora.</li>
          )}
        </ul>
      </div>

      <div className="mt-3 rounded border border-border bg-muted/20 p-3"
           data-testid={`opportunity-recommendation-${index}`}>
        <p className="text-xs uppercase tracking-wide text-muted-foreground">
          Preporuka (samo nacrt; nije dokaz, nije pregledano)
        </p>
        <p className="mt-1 text-sm">Prije odluke o sljedećem koraku pregledajte navedene izvore i otvorena pitanja. Ova preporuka ne pokreće nikakvu radnju.</p>
      </div>
      <details className="mt-4 rounded border border-border p-3 text-xs text-muted-foreground">
        <summary className="cursor-pointer">Tehnički podaci i izvorna preporuka</summary>
        <p className="mt-2 break-all">Šifre razloga: {record.reason_codes.join(", ")}</p>
        <p className="mt-2">{record.recommended_next_action}</p>
      </details>
    </div>
  );
}
